import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  RefreshCw,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  ArrowUpDown,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API, PLATFORMS, getPlatformName } from "./constants";

const EVENT_CONFIG = {
  sync_started:        { label: "Started",   color: "bg-blue-500/15 text-blue-600 border-blue-500/30",   icon: Loader2 },
  sync_completed:      { label: "Completed", color: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30", icon: CheckCircle2 },
  sync_error:          { label: "Error",     color: "bg-red-500/15 text-red-600 border-red-500/30",     icon: XCircle },
  enrichment_complete: { label: "Enriched",  color: "bg-purple-500/15 text-purple-600 border-purple-500/30", icon: CheckCircle2 },
};

const SyncLogViewer = ({ lang = "ko" }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterEvent, setFilterEvent] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const labels = {
    title: lang === "ko" ? "동기화 로그" : lang === "zh" ? "同步日志" : "Sync Logs",
    noLogs: lang === "ko" ? "동기화 로그가 없습니다" : lang === "zh" ? "暂无同步日志" : "No sync logs yet",
    noLogsDesc: lang === "ko" ? "계정 동기화를 실행하면 여기에 로그가 표시됩니다." : lang === "zh" ? "运行账户同步后日志将显示在此处。" : "Sync logs will appear here after running account syncs.",
    platform: lang === "ko" ? "플랫폼" : lang === "zh" ? "平台" : "Platform",
    all: lang === "ko" ? "전체" : lang === "zh" ? "全部" : "All",
    event: lang === "ko" ? "이벤트" : lang === "zh" ? "事件" : "Event",
    account: lang === "ko" ? "계정" : lang === "zh" ? "账户" : "Account",
    time: lang === "ko" ? "시간" : lang === "zh" ? "时间" : "Time",
    message: lang === "ko" ? "메시지" : lang === "zh" ? "消息" : "Message",
    posts: lang === "ko" ? "게시물" : lang === "zh" ? "帖子" : "Posts",
    refresh: lang === "ko" ? "새로고침" : lang === "zh" ? "刷新" : "Refresh",
    prev: lang === "ko" ? "이전" : lang === "zh" ? "上一页" : "Previous",
    next: lang === "ko" ? "다음" : lang === "zh" ? "下一页" : "Next",
    showing: lang === "ko" ? "표시 중" : lang === "zh" ? "显示中" : "Showing",
    of: lang === "ko" ? "중" : lang === "zh" ? "/" : "of",
    total: lang === "ko" ? "총" : lang === "zh" ? "共" : "total",
    started: lang === "ko" ? "시작" : lang === "zh" ? "开始" : "Started",
    completed: lang === "ko" ? "완료" : lang === "zh" ? "完成" : "Completed",
    error: lang === "ko" ? "오류" : lang === "zh" ? "错误" : "Error",
    enriched: lang === "ko" ? "보강" : lang === "zh" ? "补充" : "Enriched",
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: page * pageSize };
      if (filterPlatform) params.platform = filterPlatform;
      if (filterEvent) params.event_type = filterEvent;
      const res = await axios.get(`${API}/sync/logs`, { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterPlatform, filterEvent]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatTime = (ts) => {
    if (!ts) return "-";
    try {
      return format(new Date(ts), "MM/dd HH:mm:ss");
    } catch {
      return ts;
    }
  };

  const getEventLabel = (type) => {
    const map = { sync_started: labels.started, sync_completed: labels.completed, sync_error: labels.error, enrichment_complete: labels.enriched };
    return map[type] || type;
  };

  const totalPages = Math.ceil(total / pageSize);

  // Compute summary stats from current logs
  const stats = logs.reduce(
    (acc, log) => {
      if (log.event_type === "sync_completed") { acc.completed++; acc.totalPosts += log.posts_synced || 0; }
      if (log.event_type === "sync_error") acc.errors++;
      return acc;
    },
    { completed: 0, errors: 0, totalPosts: 0 }
  );

  return (
    <div className="space-y-6" data-testid="sync-log-viewer">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{labels.completed}</p>
                <p className="text-lg font-bold font-mono">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center">
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{labels.error}</p>
                <p className="text-lg font-bold font-mono">{stats.errors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <ArrowUpDown className="h-4 w-4 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{labels.posts}</p>
                <p className="text-lg font-bold font-mono">{stats.totalPosts.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Refresh */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {labels.title}
              <span className="text-xs text-muted-foreground font-normal ml-1">({labels.total} {total})</span>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Platform Filter */}
              <select
                value={filterPlatform}
                onChange={(e) => { setFilterPlatform(e.target.value); setPage(0); }}
                className="h-8 text-xs rounded-md border border-border bg-background px-2"
                data-testid="sync-log-platform-filter"
              >
                <option value="">{labels.all} {labels.platform}</option>
                {PLATFORMS.map((p) => (
                  <option key={p.id} value={p.id}>{getPlatformName(p.id, lang)}</option>
                ))}
              </select>
              {/* Event Filter */}
              <select
                value={filterEvent}
                onChange={(e) => { setFilterEvent(e.target.value); setPage(0); }}
                className="h-8 text-xs rounded-md border border-border bg-background px-2"
                data-testid="sync-log-event-filter"
              >
                <option value="">{labels.all} {labels.event}</option>
                <option value="sync_started">{labels.started}</option>
                <option value="sync_completed">{labels.completed}</option>
                <option value="sync_error">{labels.error}</option>
                <option value="enrichment_complete">{labels.enriched}</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchLogs} className="h-8 text-xs" data-testid="sync-log-refresh">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                {labels.refresh}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 px-4">
              <RefreshCw className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">{labels.noLogs}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">{labels.noLogsDesc}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50 bg-muted/30">
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">{labels.time}</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">{labels.event}</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">{labels.platform}</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">{labels.account}</th>
                      <th className="text-left py-2 px-4 text-xs font-medium text-muted-foreground">{labels.message}</th>
                      <th className="text-right py-2 px-4 text-xs font-medium text-muted-foreground">{labels.posts}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const cfg = EVENT_CONFIG[log.event_type] || EVENT_CONFIG.sync_started;
                      const Icon = cfg.icon;
                      return (
                        <tr key={log.id} className="border-b border-border/20 hover:bg-muted/20 transition-colors" data-testid={`sync-log-row-${log.id}`}>
                          <td className="py-2 px-4 font-mono text-xs text-muted-foreground whitespace-nowrap">{formatTime(log.timestamp)}</td>
                          <td className="py-2 px-4">
                            <Badge variant="outline" className={`text-[10px] ${cfg.color}`}>
                              <Icon className="h-3 w-3 mr-1" />
                              {getEventLabel(log.event_type)}
                            </Badge>
                          </td>
                          <td className="py-2 px-4">
                            <Badge variant="outline" className="text-[10px] capitalize">{log.platform}</Badge>
                          </td>
                          <td className="py-2 px-4 text-xs max-w-[150px] truncate">{log.account_name || "-"}</td>
                          <td className="py-2 px-4 text-xs max-w-[250px] truncate">
                            {log.error ? (
                              <span className="text-red-500">{log.error}</span>
                            ) : (
                              log.message || "-"
                            )}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-xs">
                            {log.posts_synced > 0 ? log.posts_synced.toLocaleString() : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/30">
                  <span className="text-xs text-muted-foreground">
                    {labels.showing} {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} {labels.of} {total}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} className="h-7 text-xs">
                      {labels.prev}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="h-7 text-xs">
                      {labels.next}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SyncLogViewer;
